import type { LocationMatch } from '../schemas/locations';

// Buildings shaped as the address search delivers them: an INTEGER id, and an address taken
// verbatim from the DB — the street type spelled out («Calle Alcalá 318», never «C/»), the
// casing whatever was typed in, and the matrícula appended inside the string when two
// locations would otherwise read identically. The list is one row per ADDRESS, so a building
// with several portals appears several times under one id.
const FIXTURES: LocationMatch[] = [
  { id: 1274, address: 'Calle Alcalá 318, 28027 Madrid' },
  { id: 1288, address: 'Calle Alcalá 210, 28028 Madrid' },
  { id: 1301, address: 'Calle Alcalá 33, 28014 Madrid' },
  { id: 1342, address: 'Plaza de Cervantes 6, 28801 Alcalá de Henares' },
  // One building, two portals — two pickable rows that open the same location.
  { id: 2892, address: 'Calle Sierra De Atapuerca 39 A, 28050 Madrid' },
  { id: 2892, address: 'Calle Sierra De Atapuerca 39 B, 28050 Madrid' },
  // The same street, entered in a different case: both spellings live in the table.
  { id: 3110, address: 'Calle SIERRA DE ATAPUERCA 41, 28050 Madrid' },
  // Two different locations whose address string collides, told apart by the matrícula.
  { id: 4021, address: 'Calle Bolivia 39, 28020 Madrid (BNB00)' },
  { id: 4022, address: 'Calle Bolivia 39, 28020 Madrid (BNB01)' },
];

// Matrícula per building, which the address search itself does NOT return: that read is one row
// per ADDRESS and carries no code. It is needed anyway, because backend reassigns `location_code`
// when a «Usuario» moves ([AS-023]) and the panel neither sends nor computes it — so imitating the
// reassignment takes a table of our own. The two «Bolivia 39» rows keep the codes their own address
// strings already spell out, which is the one place the fixture grounds these values.
const LOCATION_CODES: Record<number, string> = {
  1274: 'ALC18',
  1288: 'ALC10',
  1301: 'ALC33',
  1342: 'CRV06',
  2892: 'ATP39',
  3110: 'ATP41',
  4021: 'BNB00',
  4022: 'BNB01',
};

// A building as the rest of the mock needs it: what the address search returns PLUS the matrícula.
export interface MockLocation extends LocationMatch {
  locationCode: string;
}

// The MAIN PORTAL for an id, or null for an id the fixture does not know. Modelled as the FIRST
// row registered under it, which is the honest imitation of the delivered behaviour: on a change of
// building backend RELEASES whichever portal the «Usuario» had chosen, so the address that comes
// back is the new building's main portal — not necessarily the row the agent picked, since the
// search returns one row per portal and several can share an `id` (id 2892 here has two). Confirmed
// by backend on 2026-09-10 (CBX-11384), where the panel had it down as an open question.
export function findLocationInMock(id: number): MockLocation | null {
  const match = FIXTURES.find((location) => location.id === id);
  const locationCode = LOCATION_CODES[id];
  if (match === undefined || locationCode === undefined) return null;
  return { ...match, locationCode };
}

// Reserved query, so the failed-search state can be exercised in a dev run.
const ERROR_QUERY = 'error';

// Accent- and case-insensitive substring match: a placeholder for the dev experience, not a
// claim about the contract — app-service owns the real matching semantics.
const fold = (text: string): string =>
  text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

export async function fetchLocationsMock(query: string): Promise<LocationMatch[]> {
  // Simulate network latency for a realistic dev experience.
  await new Promise((resolve) => setTimeout(resolve, 100));
  const needle = fold(query.trim());
  if (needle === ERROR_QUERY) {
    throw new Error('locations search unavailable');
  }
  return FIXTURES.filter((location) => fold(location.address).includes(needle));
}
